/**
 * ColorInput - Reusable color picker with hex text input and validation
 *
 * Features:
 * - Native color picker
 * - Hex text input with validation
 * - Red border on invalid input
 * - Only commits valid colors
 * - Syncs picker and text input
 *
 * Extracted from TextElement.tsx for reuse across all editor elements.
 */

import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { StyledTextField } from '@modules/ui-components';
import { isValidHexColor } from './utils';
import {
  STYLE_FIELD_HEIGHT,
  COLOR_PICKER_SIZE,
  fieldInputSx,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';

/**
 * Props for ColorInput component
 */
export interface ColorInputProps {
  /** Current color value (hex format) */
  value: string;
  /** Callback when color changes (only valid colors) */
  onChange: (value: unknown) => void;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Test ID for the color input container */
  testId?: string;
}

/**
 * ColorInput - Color picker with hex text input and validation
 */
export const ColorInput: React.FC<ColorInputProps> = ({
  value,
  onChange,
  disabled = false,
  testId,
}) => {
  // Local state for text input (allows typing invalid values temporarily)
  const [textValue, setTextValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  // Sync with external value changes
  React.useEffect(() => {
    setTextValue(value);
    setIsValid(isValidHexColor(value));
  }, [value]);

  // Handle text input change
  const handleTextChange = useCallback(
    (newValue: string) => {
      // Always update local state for typing
      setTextValue(newValue);

      // Validate and update parent only if valid
      const valid = isValidHexColor(newValue);
      setIsValid(valid);

      if (valid) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  // Handle color picker change (always valid)
  const handlePickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newColor = e.target.value;
      setTextValue(newColor);
      setIsValid(true);
      onChange(newColor);
    },
    [onChange]
  );

  // Handle blur - reset to last valid value if invalid
  const handleBlur = useCallback(() => {
    if (!isValid) {
      setTextValue(value);
      setIsValid(true);
    }
  }, [isValid, value]);

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: FIELD_GAP_SMALL, alignItems: 'center', height: STYLE_FIELD_HEIGHT }}
    >
      <Box
        component="input"
        type="color"
        value={isValid ? textValue : value}
        onChange={handlePickerChange}
        disabled={disabled}
        sx={{
          width: COLOR_PICKER_SIZE,
          height: COLOR_PICKER_SIZE,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          padding: 0,
        }}
      />
      <StyledTextField
        value={textValue}
        onChange={handleTextChange}
        onBlur={handleBlur}
        disabled={disabled}
        size="small"
        variant="standard"
        placeholder="#ffffff"
        // `fullWidth` + `minWidth: 0`: without them StyledTextField renders its
        // default responsive width (320px at md+), which as a flex item with
        // `min-width: auto` becomes a MINIMUM — every color cell then forces a
        // 344px min-content and the `1fr 1fr` style grids overflow the editor
        // panel (right column clipped). The hex field must be free to shrink.
        fullWidth
        sx={{
          ...fieldInputSx,
          flex: 1,
          minWidth: 0,
          ...(!isValid && !disabled && {
            '& .MuiInput-underline:before': {
              borderBottomColor: 'error.main',
            },
          }),
        }}
      />
    </Box>
  );
};

export default ColorInput;
